import { DEFAULT_PAGE_SIZE, SortDirection } from '@/components/Table/types';
import { HttpClient } from '@/core/http/http-client';
import {
  mapLabourTimeAutomatedList,
  mapTotalReturnList,
} from '@/shared/mappers/kpi-mapper';
import {
  ApiResponse,
  BusinessAreaPagination,
  DataWithPagination,
  LabourTimeAutomatedDetailsJson,
  Page,
  TotalReturnDetailsJson,
} from '@/shared/types';
import { DateUtility } from '@/utils/date.utils';
import { DateRange } from 'react-day-picker';
import {
  BusinessAreaModel,
  ParentableBusinessArea,
  UserBusinessArea,
} from '../domain';
import {
  businessAreasMapper,
  businessAreasWithPaginationMapper,
  mapBusinessAreaJsonToBusinessAreaModel,
  mapUserBusinessAreasPage,
} from '../mappers';
import {
  ApiResponseBusinessArea,
  ApiResponseBusinessAreas,
  BusinessAreaAutomationsPerformanceJson,
  BusinessAreaJson,
  PagedBusinessAreas,
  ParentableBusinessAreaJson,
  UserBusinessAreaJson,
} from '../types';
import { AttachUserBusinessAreaModel } from '../types/attach-user-businessArea-model';
import { DisassociateBusinessAreaUserModel } from '../types/disassociate-business-area-user-model';
import { BusinessAreaRepository } from './businessArea.repository';

export class HttpBusinessAreaRepository extends BusinessAreaRepository {
  constructor(private readonly client: HttpClient) {
    super();
  }

  async createBusinessArea(
    createBusinessAreasJson: BusinessAreaJson,
  ): Promise<BusinessAreaModel> {
    const createdBusinessAreaJson = await this.client.post<BusinessAreaJson>({
      url: '',
      body: createBusinessAreasJson,
    });
    return mapBusinessAreaJsonToBusinessAreaModel(createdBusinessAreaJson);
  }

  async getAllBusinessAreas(
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<BusinessAreaPagination<BusinessAreaModel[]>> {
    const businessAreasJson = await this.client.get<
      ApiResponseBusinessAreas<PagedBusinessAreas>
    >({
      url: '/me/business-areas',
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return businessAreasWithPaginationMapper(businessAreasJson);
  }

  async getBusinessArea(businessAreaId: number): Promise<BusinessAreaModel> {
    const businessAreaJson = await this.client.get<
      ApiResponseBusinessArea<BusinessAreaJson>
    >({
      url: `${businessAreaId}`,
    });

    return mapBusinessAreaJsonToBusinessAreaModel(
      businessAreaJson?.data.businessArea,
    );
  }

  async getAutomationsPerformanceById(
    businessAreaId: number,
    dateRange?: DateRange,
  ): Promise<BusinessAreaAutomationsPerformanceJson> {
    const response = await this.client.get<
      ApiResponse<BusinessAreaAutomationsPerformanceJson>
    >({
      url: `${businessAreaId}/automations-performance`,
      query: {
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
      },
    });

    return mapBusinessAreaJsonToBusinessAreaModel(response.data);
  }

  async getParentableBusinessAreas(
    companyId: number,
    businessAreaId?: number,
  ): Promise<ParentableBusinessArea[]> {
    const businessAreasJson = await this.client.get<
      ApiResponseBusinessAreas<Array<ParentableBusinessAreaJson>>
    >({
      url: `eligible-parent`,
      query: {
        companyId,
        businessAreaId,
      },
    });
    return businessAreasJson.data.businessAreas.map((businessArea) => ({
      id: businessArea.id ?? 0,
      name: businessArea.name ?? '',
    }));
  }

  async enableBusinessArea(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/enable`,
    });
  }

  async attachUserBusinessArea(
    data: AttachUserBusinessAreaModel,
  ): Promise<void> {
    return await this.client.post({
      body: data,
      url: `associate-user`,
    });
  }

  async disableBusinessArea(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/disable`,
    });
  }

  async updateBusinessArea(data: BusinessAreaJson): Promise<BusinessAreaModel> {
    const updatedBusinessArea = await this.client.put<BusinessAreaJson>(
      `${data.id}`,
      data,
    );
    return mapBusinessAreaJsonToBusinessAreaModel(updatedBusinessArea);
  }

  async disassociateUser(
    data: DisassociateBusinessAreaUserModel,
  ): Promise<void> {
    return await this.client.post({
      body: data,
      url: `disassociate-user`,
    });
  }

  async getBusinessAreasByCompany(
    companyId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<BusinessAreaModel[]>> {
    const businessAreas = await this.client.get<
      ApiResponseBusinessAreas<PagedBusinessAreas>
    >({
      url: `/companies/${companyId}/business-areas`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return businessAreasWithPaginationMapper(businessAreas);
  }

  async getAllBusinessAreasByCompany(
    companyId: number,
  ): Promise<BusinessAreaModel[]> {
    const businessAreas = await this.client.get<
      ApiResponseBusinessAreas<BusinessAreaJson[]>
    >({
      url: `/companies/${companyId}/business-areas/base-info`,
    });

    return businessAreasMapper(businessAreas.data.businessAreas);
  }

  async getBusinessAreasByOrganization(
    organizationId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<BusinessAreaPagination<BusinessAreaModel[]>> {
    const businessAreas = await this.client.get<
      ApiResponseBusinessAreas<PagedBusinessAreas>
    >({
      url: `/organizations/${organizationId}/business-areas`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return businessAreasWithPaginationMapper(businessAreas);
  }

  async getBusinessAreasByUser(
    userId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<UserBusinessArea[]>> {
    const response = await this.client.get<
      ApiResponseBusinessAreas<Page<UserBusinessAreaJson>>
    >({
      url: `/users/${userId}/business-areas`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapUserBusinessAreasPage(response.data.businessAreas);
  }

  async deleteBusinessArea(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.delete(`${id}`);
  }

  async getBusinessAreaLabourTimeAutomatedById(
    businessAreaId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
  ) {
    const response = await this.client.get<
      ApiResponse<LabourTimeAutomatedDetailsJson>
    >({
      url: `${businessAreaId}/labour-time-automated`,
      query: {
        page,
        filter,
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
      },
    });

    return mapLabourTimeAutomatedList(
      response.data.executions,
      response.data.totals,
    );
  }

  async getBusinessAreaTotalReturnById(
    businessAreaId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
  ) {
    const response = await this.client.get<ApiResponse<TotalReturnDetailsJson>>(
      {
        url: `${businessAreaId}/total-return`,
        query: {
          page,
          filter,
          ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
        },
      },
    );

    return mapTotalReturnList(response.data.executions, response.data.totals);
  }
}
